import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  NgZone,
  afterNextRender,
  booleanAttribute,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';

/** Stap van de pijltjestoetsen. */
const KEYBOARD_STEP_PX = 24;
/** End-toets: vraagt "zo breed als kan", de layout klemt dat tot zijn maximum. */
const KEYBOARD_MAX_REQUEST_PX = 100_000;
const DEFAULT_LABEL = 'Sleep om het paneel breder of smaller te maken. Dubbelklik zet het terug op de standaardbreedte.';
const DEFAULT_RESET_LABEL = 'Terug naar de standaardbreedte';
const DEFAULT_FIT_LABEL = 'Zo breed als de inhoud nodig heeft';
/** Ruim de dubbelkliktijd van Windows (500 ms). */
const FIT_DOUBLE_CLICK_GRACE_MS = 600;

interface DragStart {
  readonly pointerId: number;
  readonly clientX: number;
  readonly width: number;
}

/**
 * DE sleeplijn tussen twee naast elkaar liggende kolommen: slepen past de breedte aan
 * van het paneel dat er direct op volgt (het volgende element in de DOM). Gebruik deze
 * component voor elke versleepbare kolomgrens; bouw de pointer-afhandeling niet opnieuw
 * na (CLAUDE.md, "Reuse before building").
 *
 * - `size` is de gekozen breedte in px, `undefined` = de standaardbreedte van de layout.
 *   De gebruiker zet hem als CSS custom property op de layout en klemt hem daar
 *   (minimum, ruimte voor de andere kolom); tijdens het slepen wordt hij live bijgewerkt.
 * - `sizeCommitted` vuurt op het einde van een aanpassing (loslaten, toets, dubbelklik),
 *   met de breedte die het paneel na het klemmen echt kreeg: het moment om te bewaren.
 *   Een breedte tot en met `minSize` telt als standaard (`undefined`).
 * - Terug naar de standaard: het knopje dat op de lijn verschijnt zolang het paneel
 *   breder staat, een dubbelklik op de lijn, of Home. Pijltjes verbreden/versmallen per
 *   24px, End maakt het paneel zo breed als de layout toelaat.
 * - Op maat van de inhoud: met `contentShortfall` verschijnt onder de greep een knopje
 *   dat het paneel net zo breed maakt als zijn inhoud nodig heeft (hooguit zo breed
 *   als de layout toelaat).
 * - `available` op `false` verbergt lijn en greep (bv. zolang de inhoud niets aan
 *   meer breedte heeft); het element blijft staan, dus de goot en de layout ook.
 */
@Component({
  selector: 'e-resize-handle',
  templateUrl: './e-resize-handle.component.html',
  styleUrl: './e-resize-handle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'separator',
    'aria-orientation': 'vertical',
    '[attr.tabindex]': 'available() ? 0 : -1',
    '[attr.aria-hidden]': 'available() ? null : true',
    '[attr.title]': 'label()',
    '[attr.aria-label]': 'label()',
    '[attr.aria-valuemin]': 'minSize()',
    '[attr.aria-valuenow]': 'size() ?? minSize()',
    '[class.is-dragging]': 'dragging()',
    '[class.is-unavailable]': '!available()',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointerup)': 'onPointerEnd($event)',
    '(pointercancel)': 'onPointerEnd($event)',
    '(lostpointercapture)': 'onPointerEnd($event)',
    '(dblclick)': 'onLineDoubleClick()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class EResizeHandleComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);

  /** Standaard- en minimumbreedte van het paneel (px). */
  readonly minSize = input.required<number>();
  readonly label = input(DEFAULT_LABEL);
  readonly resetLabel = input(DEFAULT_RESET_LABEL);
  readonly fitLabel = input(DEFAULT_FIT_LABEL);
  /** `false`: lijn en greep verborgen en niet bedienbaar (de goot blijft). */
  readonly available = input(true, { transform: booleanAttribute });
  /**
   * Zolang de inhoud van het paneel breder is dan het paneel: een meting van hoeveel
   * px het paneel tekortkomt om die zonder horizontale scrollbalk te tonen. Toont het
   * knopje "op maat van de inhoud" onder de greep; `undefined` = de inhoud past.
   */
  readonly contentShortfall = input<() => number>();
  /** Gekozen breedte in px; `undefined` = standaard. Tijdens het slepen live bijgewerkt. */
  readonly size = model<number | undefined>();
  /** Einde van een aanpassing, met de echte (geklemde) breedte: hier bewaren. */
  readonly sizeCommitted = output<number | undefined>();

  protected readonly dragging = signal(false);
  /**
   * De laatste aanpassing liep tegen het maximum van de layout aan: breder kan niet,
   * dus het knopje "op maat van de inhoud" zou niets meer doen.
   */
  protected readonly atMaximum = signal(false);
  /** Laatste klik op "op maat" (zie {@link onLineDoubleClick}). */
  private lastFitAt = Number.NEGATIVE_INFINITY;
  private dragStart: DragStart | undefined;
  private pendingFrame: number | undefined;
  private pendingSize: number | undefined;
  private removeMoveListener: (() => void) | undefined;

  constructor() {
    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => this.stopDrag());
    // Een ander venster geeft de layout een ander maximum: "op maat" mag weer.
    const view = this.host.ownerDocument.defaultView;
    const onWindowResize = (): void => {
      if (this.atMaximum()) this.ngZone.run(() => this.atMaximum.set(false));
    };
    this.ngZone.runOutsideAngular(() => view?.addEventListener('resize', onWindowResize, { passive: true }));
    destroyRef.onDestroy(() => view?.removeEventListener('resize', onWindowResize));
  }

  protected onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !this.available()) return;
    const pane = this.pane();
    if (pane == null) return;
    // Geen tekstselectie en geen focus-sprong tijdens het slepen.
    event.preventDefault();
    this.stopDrag();
    this.dragStart = { pointerId: event.pointerId, clientX: event.clientX, width: pane.getBoundingClientRect().width };
    try {
      this.host.setPointerCapture(event.pointerId);
    } catch {
      // Synthetische pointers (specs) hebben geen capture; slepen werkt ook zonder.
    }
    this.dragging.set(true);
    // Buiten de zone: elke pointermove zou anders een volledige change detection geven.
    // De breedte gaat hooguit één keer per frame terug naar binnen.
    const moveListener = (moveEvent: PointerEvent): void => this.onPointerMove(moveEvent);
    this.ngZone.runOutsideAngular(() => this.host.addEventListener('pointermove', moveListener));
    this.removeMoveListener = () => this.host.removeEventListener('pointermove', moveListener);
  }

  protected onPointerEnd(event: PointerEvent): void {
    const start = this.dragStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    const finalSize = this.pendingSize;
    this.stopDrag();
    if (this.host.hasPointerCapture(start.pointerId)) this.host.releasePointerCapture(start.pointerId);
    // Een klik zonder beweging (ook de eerste helft van een dubbelklik) verandert niets.
    if (finalSize == null) return;
    this.size.set(finalSize);
    this.commitAfterRender();
  }

  /** De knopjes op de lijn: geen sleep starten en de dubbelklik van de lijn niet nog eens. */
  protected onButtonPointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  protected onLineDoubleClick(): void {
    // Het knopje "op maat" verdwijnt zodra de inhoud past: de tweede klik van een
    // dubbelklik erop landt dan op de lijn, en die dubbelklik zou meteen terugzetten.
    if (performance.now() - this.lastFitAt < FIT_DOUBLE_CLICK_GRACE_MS) return;
    this.reset();
  }

  protected reset(): void {
    if (!this.available()) return;
    this.atMaximum.set(false);
    this.size.set(undefined);
    this.sizeCommitted.emit(undefined);
  }

  /** Breder met wat de inhoud tekortkomt; de layout klemt dat tot zijn maximum. */
  protected fitToContent(): void {
    const measureShortfall = this.contentShortfall();
    const pane = this.pane();
    if (measureShortfall == null || pane == null || !this.available()) return;
    this.lastFitAt = performance.now();
    const shortfall = measureShortfall();
    if (shortfall <= 0) return;
    this.size.set(Math.ceil(pane.getBoundingClientRect().width + shortfall));
    this.commitAfterRender();
  }

  protected onKeydown(event: KeyboardEvent): void {
    const pane = this.pane();
    if (pane == null || !this.available()) return;
    const width = pane.getBoundingClientRect().width;
    switch (event.key) {
      case 'ArrowLeft':
        this.size.set(Math.round(width + KEYBOARD_STEP_PX));
        break;
      case 'ArrowRight':
        this.size.set(Math.round(width - KEYBOARD_STEP_PX));
        break;
      case 'End':
        this.size.set(KEYBOARD_MAX_REQUEST_PX);
        break;
      case 'Home':
        event.preventDefault();
        this.reset();
        return;
      default:
        return;
    }
    event.preventDefault();
    this.commitAfterRender();
  }

  private onPointerMove(event: PointerEvent): void {
    const start = this.dragStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    // Het paneel ligt rechts van de lijn: naar links slepen maakt het breder.
    this.pendingSize = Math.round(Math.max(this.minSize(), start.width + start.clientX - event.clientX));
    this.pendingFrame ??= requestAnimationFrame(() => {
      this.pendingFrame = undefined;
      const size = this.pendingSize;
      if (size != null) this.ngZone.run(() => this.size.set(size));
    });
  }

  /**
   * Na de volgende render staat de nieuwe breedte in de layout en heeft die hem
   * geklemd: de echte breedte meten en die doorgeven (en terugzetten, zodat een
   * te ver gesleepte waarde niet blijft hangen).
   */
  private commitAfterRender(): void {
    afterNextRender(
      {
        read: () => {
          const pane = this.pane();
          if (pane == null) return;
          const requested = this.size();
          const width = Math.round(pane.getBoundingClientRect().width);
          this.atMaximum.set(requested != null && width < requested);
          const committed = width <= this.minSize() ? undefined : width;
          this.size.set(committed);
          this.sizeCommitted.emit(committed);
        },
      },
      { injector: this.injector },
    );
  }

  private stopDrag(): void {
    this.removeMoveListener?.();
    this.removeMoveListener = undefined;
    if (this.pendingFrame != null) cancelAnimationFrame(this.pendingFrame);
    this.pendingFrame = undefined;
    this.pendingSize = undefined;
    this.dragStart = undefined;
    this.dragging.set(false);
  }

  /** Het paneel dat de lijn versleept: het element direct na de lijn. */
  private pane(): HTMLElement | undefined {
    const next = this.host.nextElementSibling;
    return next instanceof HTMLElement ? next : undefined;
  }
}
